// A gas station — the pump where vehicles refuel. A fuel pump on an island with
// a hose, a storage tank and a small flat canopy on a post. Ported 1:1 from
// age-of-mcp's gas_station.ts: local pump()/canopy() generators plus the shared
// foundationSlab/scaffold helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, type Part } from "../../lib";

const STONE = "#5a5a5a";
const ISLAND = "#9a9a96";
const PUMP = "#c43b2f";
const PANEL = "#e8e4d8";
const HOSE = "#26211b";
const TANK = "#7a8288";
const CANOPY = "#3a5a7a";
const POST = "#cfcabb";

const BASE = 0.14;

function pump(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.34, 0.05, 0.26), color: ISLAND, position: [0, BASE + 0.02, 0.05] },
    { geometry: new THREE.BoxGeometry(0.16, 0.4, 0.14), color: PUMP, position: [-0.04, BASE + 0.24, 0.05] },
    { geometry: new THREE.BoxGeometry(0.12, 0.1, 0.02), color: PANEL, position: [-0.04, BASE + 0.36, 0.13] },
    { geometry: new THREE.CylinderGeometry(0.02, 0.02, 0.18, 6), color: HOSE, position: [0.06, BASE + 0.2, 0.08], rotation: [0, 0, 0.5] },
    { geometry: new THREE.BoxGeometry(0.04, 0.08, 0.04), color: HOSE, position: [0.11, BASE + 0.12, 0.08] },
  ];
}

function canopy(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.03, 0.03, 0.66, 6), color: POST, position: [0.18, BASE + 0.33, -0.14] },
    { geometry: new THREE.BoxGeometry(0.6, 0.06, 0.5), color: CANOPY, position: [0, BASE + 0.66, 0.0] },
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.5)];
  if (s === 1) return [foundationSlab(0.5), { geometry: new THREE.CylinderGeometry(0.16, 0.16, 0.3, 12), color: TANK, position: [0.16, BASE + 0.15, -0.16] }, ...scaffold(0.44, 0.5)];
  if (s === 2) return [foundationSlab(0.5), { geometry: new THREE.CylinderGeometry(0.16, 0.16, 0.34, 12), color: TANK, position: [0.16, BASE + 0.17, -0.16] }, ...pump(), ...scaffold(0.44, 0.8)];
  return [
    { geometry: new THREE.BoxGeometry(0.5, BASE, 0.5), color: STONE, position: [0, BASE / 2, 0] },
    { geometry: new THREE.CylinderGeometry(0.16, 0.16, 0.36, 12), color: TANK, position: [-0.18, BASE + 0.18, -0.16], rotation: [Math.PI / 2, 0, 0] },
    ...pump(),
    ...canopy(),
  ];
}

export const gasStation = defineModel({
  id: "gas-station",
  tiles: { w: 1, h: 1 },
  batchId: "standard",
  name: "Gas station",
  tags: ["building","gas","station","low-poly","age-of-mcp","classic"],
  summary: "Low-poly gas station.",
  description: "A fuel pump where vehicles refuel, holding a stock of fuel.",
  author: "age-of-mcp",
  footprint: 0.42,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
